import * as React from 'react';
import styles from '~/CzReimbursement/styles.less';
import { DebounceInput } from '~/Debounce';
import { TailwindTooltip } from '~/libs/ui/atoms/TailwindTooltip';
import Checkbox from '~/UI/Checkbox/Checkbox';
import Icon from '~/UI/Icon';
import { Input } from '~/UI/Input';
import inputCompactStyles from '~/UI/Input/compact.less';

export interface SettingsValues {
	readonly dosageStrength: string;
	readonly packageSize: string;
	readonly selected: boolean;
	readonly usualTherapeuticDailyDose: string;
	readonly usualTherapeuticDailyDosePerPackage: string;
}

export interface SettingsErrors {
	readonly dosageStrength?: boolean;
	readonly packageSize?: boolean;
	readonly usualTherapeuticDailyDose?: boolean;
	readonly usualTherapeuticDailyDosePerPackage?: boolean;
}

export const EmptySettingsVaslues: SettingsValues = {
	dosageStrength: '',
	packageSize: '',
	selected: true,
	usualTherapeuticDailyDose: '',
	usualTherapeuticDailyDosePerPackage: '',
};

const SettingInput: React.FunctionComponent<
	React.PropsWithChildren<{
		label: string;
		showHeader: boolean;
		value: string;
		isInvalid: boolean;
		onChange(value: string): void;
	}>
> = ({ label, showHeader, value, isInvalid, onChange }) => {
	return (
		<div className={showHeader ? styles.groupListInputContainerWithHeader : styles.groupListInputContainer}>
			{showHeader ? <span>{label}</span> : null}
			<DebounceInput value={value} onBlur={() => {}} onChange={onChange}>
				<Input isInvalid={isInvalid} styles={inputCompactStyles} />
			</DebounceInput>
		</div>
	);
};

export const Settings: React.FunctionComponent<
	React.PropsWithChildren<{
		errors: SettingsErrors;
		showHeader: boolean;
		usedForCalculation: boolean;
		values: SettingsValues;
		onChange(values: SettingsValues): void;
	}>
> = ({ errors, showHeader, usedForCalculation, values, onChange }) => {
	return (
		<>
			{usedForCalculation ? (
				<TailwindTooltip
					reference={<Icon name='formula' />}
					displayClass='relative top-0.5 inline-block mr-1.5 h-4 align-top'
					position='top'
				>
					Used for calculation
				</TailwindTooltip>
			) : null}

			<SettingInput
				isInvalid={errors.packageSize ?? false}
				label='Package'
				showHeader={showHeader}
				value={values.packageSize}
				onChange={(packageSize) => {
					onChange({ ...values, packageSize });
				}}
			/>

			<SettingInput
				isInvalid={errors.dosageStrength ?? false}
				label='Dosage'
				showHeader={showHeader}
				value={values.dosageStrength}
				onChange={(dosageStrength) => {
					onChange({ ...values, dosageStrength });
				}}
			/>

			<SettingInput
				isInvalid={errors.usualTherapeuticDailyDose ?? false}
				label='ODTD'
				showHeader={showHeader}
				value={values.usualTherapeuticDailyDose}
				onChange={(usualTherapeuticDailyDose) => {
					onChange({ ...values, usualTherapeuticDailyDose });
				}}
			/>

			<SettingInput
				isInvalid={errors.usualTherapeuticDailyDosePerPackage ?? false}
				label='ODTDBAL'
				showHeader={showHeader}
				value={values.usualTherapeuticDailyDosePerPackage}
				onChange={(usualTherapeuticDailyDosePerPackage) => {
					onChange({ ...values, usualTherapeuticDailyDosePerPackage });
				}}
			/>

			<div className='absolute right-0 -mr-2 top-1 inline-block'>
				{showHeader && (
					<span className='absolute right-0 bottom-full mb-1 mr-2 text-[12px] whitespace-nowrap'>Use for calc.</span>
				)}
				<Checkbox
					checked={values.selected}
					onChange={(selected) => {
						onChange({ ...values, selected });
					}}
				/>
			</div>
		</>
	);
};

Settings.displayName = 'CzReimbursement.GroupList.Settings';
